import moment from 'moment';
import { ChatBubbleLeftEllipsisIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Rental } from '../../../services/models/rental';
import { getEquipmentLockLabel, getRentalStatusInfo } from '../utils/rentalUtils';

interface RentalHistoryListProps {
  rentals: Rental[];
  page: number;
  totalPages?: number;
  isLoading?: boolean;
  onPageChange: (page: number) => void;
  onRentalClick: (rental: Rental) => void;
}

const RentalHistoryList: React.FC<RentalHistoryListProps> = ({
  rentals,
  page,
  totalPages = 1,
  isLoading,
  onPageChange,
  onRentalClick,
}) => (
  <div className="bg-white rounded-xl shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 overflow-hidden">
    {/* Header row */}
    <div className="hidden md:grid grid-cols-[0.8fr_1.2fr_1.6fr_1.4fr_1fr_0.8fr_0.9fr] gap-4 px-5 py-3 border-b border-neutral-10 bg-neutral-5">
      <span className="typo-caption text-neutral-40 font-semibold uppercase tracking-wider">N°</span>
      <span className="typo-caption text-neutral-40 font-semibold uppercase tracking-wider">Créée le</span>
      <span className="typo-caption text-neutral-40 font-semibold uppercase tracking-wider">Période</span>
      <span className="typo-caption text-neutral-40 font-semibold uppercase tracking-wider">Loueur</span>
      <span className="typo-caption text-neutral-40 font-semibold uppercase tracking-wider">Équipements</span>
      <span className="typo-caption text-neutral-40 font-semibold uppercase tracking-wider text-right">Prix final</span>
      <span className="typo-caption text-neutral-40 font-semibold uppercase tracking-wider text-right">Statut</span>
    </div>

    {isLoading ? (
      <div className="py-12 text-center typo-small text-neutral-40">Chargement...</div>
    ) : rentals.length === 0 ? (
      <div className="py-12 text-center typo-small text-neutral-40">Aucune réservation passée</div>
    ) : (
      <ul>
        {rentals.map((rental) => {
          const status = getRentalStatusInfo(rental.rentalStatus);
          return (
            <li key={rental.id}>
              <button
                type="button"
                onClick={() => onRentalClick(rental)}
                className="w-full text-left flex flex-col gap-1.5 px-5 py-3.5 border-b border-neutral-10 last:border-b-0 hover:bg-neutral-5 transition-colors"
              >
                <div className="w-full grid grid-cols-2 md:grid-cols-[0.8fr_1.2fr_1.6fr_1.4fr_1fr_0.8fr_0.9fr] gap-2 md:gap-4 items-center">
                <span className="typo-small font-semibold text-neutral tabular-nums">
                  #{rental.publicId}
                </span>
                <span className="typo-small text-neutral-60">
                  {moment.utc(rental.createdAt).local().format('DD/MM/YY HH:mm')}
                </span>
                <span className="typo-small text-neutral">
                  {moment.utc(rental.startDateUTC).local().format('DD/MM/YY HH:mm')}
                  {' → '}
                  {moment.utc(rental.endDateUTC).local().format('DD/MM/YY HH:mm')}
                </span>
                <span className="typo-small text-neutral truncate">
                  {rental.user.firstName} {rental.user.lastName}
                </span>
                <span className="typo-small text-neutral-60 truncate">
                  {rental.equipments?.length
                    ? rental.equipments
                        .map((e) => {
                          const lockLabel = getEquipmentLockLabel(e);
                          return lockLabel ? `${lockLabel} · ${e.model}` : e.model;
                        })
                        .join(', ')
                    : '—'}
                </span>
                <span className="typo-small text-neutral text-right">
                  {rental.invoice?.finalPrice != null
                    ? `${(rental.invoice.finalPrice / 100).toFixed(2)} €`
                    : '—'}
                </span>
                <span className="flex md:justify-end">
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md typo-caption font-medium ${status.badgeClassName}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${status.dotClassName}`} />
                    {status.label}
                  </span>
                </span>
                </div>
                {rental.comment && (
                  <span className="flex items-center gap-1.5 typo-caption text-neutral-40 italic min-w-0">
                    <ChatBubbleLeftEllipsisIcon className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate">{rental.comment}</span>
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    )}

    {/* Pagination */}
    {totalPages > 1 && (
      <div className="flex items-center justify-between px-5 py-3 border-t border-neutral-10">
        <button
          type="button"
          disabled={page <= 0}
          onClick={() => onPageChange(page - 1)}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md typo-small text-neutral-60 hover:text-neutral hover:bg-neutral-5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeftIcon className="w-4 h-4" />
          Précédent
        </button>
        <span className="typo-small text-neutral-40">
          Page {page + 1} / {totalPages}
        </span>
        <button
          type="button"
          disabled={page >= totalPages - 1}
          onClick={() => onPageChange(page + 1)}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md typo-small text-neutral-60 hover:text-neutral hover:bg-neutral-5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          Suivant
          <ChevronRightIcon className="w-4 h-4" />
        </button>
      </div>
    )}
  </div>
);

export default RentalHistoryList;
